/**
 * Loads a portal read with the pages' common states: loading, a described
 * failure (retry), and a request-id guard so a slow earlier call never
 * overwrites a newer one. `key` changes re-load (the property filter).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { describePortalFailure, PortalFailure } from '../../utils/portal';
import { usePortal } from './PortalContext';

export function usePortalLoad<T>(load: () => Promise<T>, fallback: string, key = '') {
  const { reportFailure } = usePortal();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<PortalFailure | null>(null);
  const requestId = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setFailure(null);
    try {
      const result = await loadRef.current();
      if (id !== requestId.current) return;
      setData(result);
    } catch (error) {
      if (id !== requestId.current) return;
      const described = describePortalFailure(error, fallback);
      setData(null);
      setFailure(described);
      reportFailure(described);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [fallback, reportFailure]);

  // Loads on mount and again whenever `key` (the inputs of the load, e.g. the property filter) changes
  useEffect(() => {
    void reload();
  }, [reload, key]);

  return { data, loading, failure, reload, setData };
}
